import type { Ring } from '../../../geo/types';
import type { ChurchParams, SpireKind } from './churchParams';
import { spanOver, type PlanFrame } from './planFrame';

export type Side = 1 | -1;

export interface TowerPlan {
  readonly u0: number;
  readonly u1: number;
  readonly vc: number;
  readonly half: number;
  readonly height: number;
  readonly spire: SpireKind;
  readonly spireHeight: number;
}

/** Central vessel (nave and choir) from u0 to the apse chord at chordU. */
export interface VesselPlan {
  readonly u0: number;
  readonly chordU: number;
  readonly vc: number;
  readonly half: number;
  readonly eaveY: number;
  readonly ridgeY: number;
  readonly apse: 'polygonal' | 'flat';
}

/** Aisle (lean-to) or side vessel of a hall church (own gable) along one side. */
export interface SidePlan {
  readonly side: Side;
  readonly u0: number;
  readonly u1: number;
  readonly inner: number;
  readonly outer: number;
  readonly eaveY: number;
  readonly topY: number;
  readonly roof: 'leanTo' | 'gable';
}

/** Side chapel with a transverse gable facing outward. */
export interface ChapelPlan {
  readonly side: Side;
  readonly u0: number;
  readonly u1: number;
  readonly inner: number;
  readonly outer: number;
  readonly eaveY: number;
  readonly ridgeY: number;
}

export interface TranseptPlan {
  readonly u0: number;
  readonly u1: number;
  readonly v0: number;
  readonly v1: number;
  readonly eaveY: number;
  readonly ridgeY: number;
}

export interface FlechePlan {
  readonly u: number;
  readonly v: number;
  readonly footY: number;
  readonly kind: 'needle' | 'pyramid';
}

export interface ChurchPlan {
  readonly tower: TowerPlan | null;
  /** Narrower bay between the tower and the nave, flat at both ends. */
  readonly westBay: VesselPlan | null;
  readonly vessel: VesselPlan;
  readonly sides: readonly SidePlan[];
  readonly chapels: readonly ChapelPlan[];
  readonly transept: TranseptPlan | null;
  readonly fleche: FlechePlan | null;
}

const DEG = Math.PI / 180;

export const CHURCH_PLAN = {
  /** Parts may stand this far outside the traced outline (the drawn walls are approximate). */
  margin: 0.3,
  /** Buttresses project this far; walls that carry them keep the difference to the margin free. */
  buttressDepth: 1,
  step: 0.25,
  roofPitch: 58 * DEG,
  maxNaveWidth: 14,
  aisledNaveShare: 0.4,
  towerShare: 0.35,
  maxTowerWidth: 10,
  towerSearchShare: 0.25,
  needleRatio: 1.2,
  pyramidRatio: 0.5,
  /** The vessel reaches this far into the tower so no seam opens between them. */
  towerOverlap: 0.2,
  transeptExcess: 6,
  transeptMinRatio: 0.6,
  transeptMaxRatio: 1.5,
  armMinReach: 3,
  flecheSink: 1.5,
  shrinkSteps: 6,
  shrinkFactor: 0.85,
  aisleRatio: 0.75,
  leanToPitch: 26 * DEG,
  /** Share of the wall between aisle eave and nave eave kept free for clerestory windows. */
  clerestoryShare: 0.4,
  minSide: 2.5,
  /** Stretches this close to the target width take the full aisle; narrower ones step in. */
  fullSideShare: 0.8,
  minRun: 2,
  chapelBay: 6,
  minChapelDepth: 3,
  maxChapelDepth: 10,
  chapelPitch: 60 * DEG,
  chapelEaveRatio: 0.6,
  minChapelEave: 4,
  chapelClearance: 0.3,
} as const;

export const APSE_SIDE = Math.tan(22.5 * DEG);

/** Plan outline east of the chord: side-wall stubs and three facets of an octagon. */
export function apseRing(vessel: VesselPlan): Ring {
  if (vessel.apse === 'flat') return [];
  const { chordU: c, vc, half: h } = vessel;
  return [
    [c, vc - h],
    [c + APSE_SIDE * h, vc - h],
    [c + h, vc - APSE_SIDE * h],
    [c + h, vc + APSE_SIDE * h],
    [c + APSE_SIDE * h, vc + h],
    [c, vc + h],
  ];
}

export function isHall(params: ChurchParams): boolean {
  return params.aisles >= 2 && params.aisleHeight >= params.naveHeight;
}

/** Distance walls with buttresses keep from the traced outline. */
export function buttressRoom(): number {
  return CHURCH_PLAN.buttressDepth - CHURCH_PLAN.margin;
}

export function fitsBand(frame: PlanFrame, u0: number, u1: number, lo: number, hi: number): boolean {
  const span = spanOver(frame, u0, u1, (lo + hi) / 2);
  return span !== null && span[0] <= lo + CHURCH_PLAN.margin && span[1] >= hi - CHURCH_PLAN.margin;
}

